const NUMBER_OF_DIMENSIONS = 4;
export type Dimension = 0 | 1 | 2 | 3;
export type DimensionValue = 0 | 1 | 2;


type ProtoTile = DimensionValue[];


const makeProtoTiles = (
    numberOfValues: number,
): ProtoTile[] => {
    const addDimensionToProtoTiles = (smallerTiles: ProtoTile[]): ProtoTile[] => {
        const biggerTiles: ProtoTile[] = [];
        for (let tile of smallerTiles) {
            for (let value = 0; value < numberOfValues; value++) {
                const newTile = [...tile];
                newTile.push(value as DimensionValue);
                biggerTiles.push(newTile);
            }
        }
        return biggerTiles;
    }
    let tiles: ProtoTile[] = [[]];
    for (let dimension = 0; dimension < NUMBER_OF_DIMENSIONS; dimension++) {
        tiles = addDimensionToProtoTiles(tiles);
    }
    return tiles;
}


type Square<Edge> = {
    left: Edge,
    top: Edge,
    right: Edge,
    bottom: Edge,
}

export type PositionInSquare = "left" | "right" | "top" | "bottom";

export type RotationType = 'rotateClockwise' | 'rotateAnticlockwise' | 'flipHorizontal' | 'flipVertical'

export const rotated = (square: Square<any>, rotationType: RotationType) => {
    switch (rotationType) {
        case "flipHorizontal":
            return {
                left: square.left,
                top: square.bottom,
                right: square.right,
                bottom: square.top
            }
        case "flipVertical":
            return {
                left: square.right,
                top: square.top,
                right: square.left,
                bottom: square.bottom,
            }
        case "rotateAnticlockwise":
            return {
                left: square.top,
                top: square.right,
                right: square.bottom,
                bottom: square.left
            }
        case "rotateClockwise":
            return {
                left: square.bottom,
                top: square.left,
                right: square.top,
                bottom: square.right
            }
    }
}

export const tileFromProtoTile = (protoTile: ProtoTile) => {
    return {
        left: {
            dimension: 0 as Dimension,
            value: protoTile[0]
        },
        top: {
            dimension: 1 as Dimension,
            value: protoTile[1]
        },
        right: {
            dimension: 2 as Dimension,
            value: protoTile[2]
        },
        bottom: {
            dimension: 3 as Dimension,
            value: protoTile[3]
        },
    };
}


export const makeTiles = (numberOfValues: number) => {
    return makeProtoTiles(numberOfValues).map(tileFromProtoTile)
}

export type OrientedTile = Square<{
    dimension: Dimension,
    value: DimensionValue,
}>


const allOrientationsOfTile = (tile: OrientedTile): OrientedTile[] => {
    const allOrientations: OrientedTile[] = [];
    allOrientations.push(tile);
    const traversingTransformations: RotationType[] = [
        "rotateClockwise",
        "rotateClockwise",
        "rotateClockwise",
        "flipVertical",
        "rotateClockwise",
        "rotateClockwise",
        "rotateClockwise",
    ]
    for (let rotationType of traversingTransformations) {
        tile = rotated(tile, rotationType);
        allOrientations.push(tile);
    }
    return allOrientations;
}


export const allOrientationsOfTiles = (tiles: OrientedTile[]): OrientedTile[] => {
    return Array.from(tiles).flatMap(allOrientationsOfTile);
}


export type MaybeOrientedTile = OrientedTile | undefined;


export const getPlayableTile = (
    sourceTile: OrientedTile,
    placedTiles: MaybeOrientedTile[][],
    col: number,
    row: number
): MaybeOrientedTile => {
    const tilesToTry = allOrientationsOfTile(sourceTile);
    return tilesToTry.find(
        (tile: OrientedTile) => canPlayTileIn(tile, placedTiles, col, row)
    );
}


export const canPlayTileIn = (
    tile: OrientedTile,
    placedTiles: MaybeOrientedTile[][],
    col: number,
    row: number
): boolean => getReasonCannotPlay(tile, placedTiles, col, row) === 'none'


export const getReasonCannotPlay = (
    tile: OrientedTile,
    placedTiles: MaybeOrientedTile[][],
    col: number,
    row: number
): string => {
    if (placedTiles[row][col]) {
        return `Cell ${col}, ${row} is occupied`
    }
    // console.log(`Checking for a reason cannot play ${tile} in col ${col} row ${row}`);
    const SQUARE_POSITIONS: PositionInSquare[] = ['left', 'top', 'right', 'bottom'];
    for (let pair of [[0, 2], [1, 3]]) {
        const isVertical = !!pair[0];
        for (let isNot of [0, 1]) {
            let rowToCompare = row;
            let colToCompare = col;
            if (isVertical) {
                rowToCompare += isNot ? 1 : -1;
            } else {
                colToCompare += isNot ? 1 : -1;
            }
            const maybeTileToCompare = placedTiles[rowToCompare] && placedTiles[rowToCompare][colToCompare];
            if (maybeTileToCompare) {
                const tileToCompare: OrientedTile = maybeTileToCompare;
                const position = SQUARE_POSITIONS[pair[isNot]];
                const positionToCompare = SQUARE_POSITIONS[pair[1 - isNot]];
                const toCompare = tileToCompare[positionToCompare];
                // console.log(
                //     'Checking', JSON.stringify(toCompare),
                //     'which is to the', position, 'of', JSON.stringify(tile[position])
                // );
                if (toCompare.dimension !== tile[position].dimension) {
                    return `Mismatch to the ${position}: our dimension ${tile[position].dimension} should match their ${toCompare.dimension}`
                }
                if (toCompare.value !== tile[position].value) {
                    return `Mismatch to the ${position}: our value ${tile[position].value} should match their ${toCompare.value}`
                }
            } else {
                // console.log(`No tile to the ${position} to clash with`)
            }
        }
    }
    return 'none';
}